import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Link,
  Divider,
  Stack,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useQuery } from 'react-query';
import api from '../../utils/api';
import { useAuth } from '../../auth/useAuth';
import { useOnboarding } from '../OnboardingProvider';
import { withOfflineFallback, readCurrentUser } from '../../offline';
import { TierSection } from './TierSection';

interface BackendUser {
  firstName?: string;
  lastName?: string;
  name?: string;
}

export const SetupChecklistDrawer: React.FC = () => {
  const { state, drawerOpen, closeDrawer } = useOnboarding();
  const { user } = useAuth();

  // users is a replicated table — offline, the greeting comes from the replica.
  const { data: backendUser } = useQuery<BackendUser>(
    ['current-user-me'],
    withOfflineFallback(
      async () => {
        const res = await api.get('/users/me');
        return res.data;
      },
      () => readCurrentUser(user?.uid, user?.email ?? undefined) as Promise<BackendUser>,
    ),
    { staleTime: 5 * 60 * 1000 },
  );

  const greetingName =
    backendUser?.firstName ||
    backendUser?.lastName ||
    backendUser?.name?.split(' ')[0] ||
    'there';

  if (!state) return null;

  const { progress, tiers } = state;
  const isComplete = progress.percent >= 100;

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={closeDrawer}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          bgcolor: '#FAF7F1',
          borderLeft: '1px solid #E8D7BF',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#3B2E1A', lineHeight: 1.25 }}>
                {isComplete
                  ? `Beautifully done, ${greetingName}.`
                  : `Let's get you set up, ${greetingName}.`}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#6B6050', mt: 0.5, fontSize: '0.8125rem' }}
              >
                {isComplete
                  ? 'Your clinic is fully configured — back to the horses.'
                  : `Working through these is the fastest way to get real value out of StableTrack.`}
              </Typography>
            </Box>
            <IconButton
              onClick={closeDrawer}
              size="small"
              aria-label="Close onboarding drawer"
              sx={{ color: '#6B6050', mt: -0.5, mr: -1 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Progress */}
          <Box sx={{ mb: 0.75 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#6B6050', fontWeight: 600, fontSize: '0.75rem' }}>
                {progress.completed} of {progress.total} complete
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isComplete ? '#2F7D32' : '#8B4513',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                }}
              >
                {progress.percent}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress.percent}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: '#EFE5D3',
                '& .MuiLinearProgress-bar': {
                  bgcolor: isComplete ? '#2F7D32' : '#8B4513',
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#EDE3D3' }} />

        {/* Tier sections */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          <Stack spacing={2}>
            {tiers.map((tier) => (
              <TierSection key={tier.tier} tier={tier} />
            ))}
          </Stack>
        </Box>

        {/* Footer */}
        <Divider sx={{ borderColor: '#EDE3D3' }} />
        <Box sx={{ px: 3, py: 2, textAlign: 'center' }}>
          <Link
            component="button"
            onClick={closeDrawer}
            sx={{
              fontSize: '0.75rem',
              color: '#8B6B4A',
              textDecorationColor: 'rgba(139,107,74,0.4)',
              '&:hover': { color: '#6B4F2E', textDecorationColor: '#6B4F2E' },
            }}
          >
            Close — I'll come back to this
          </Link>
        </Box>
      </Box>
    </Drawer>
  );
};
